import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    TemplateRef,
    ViewChild,
    inject
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProductComponentCrosssellsDef } from '@df/catalog-ui/product/product.component.crosssells.def';
import { Category } from '@df/catalog/category/category';
import { EdependencyLevel } from '@df/catalog/model';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { AdvancedCommerceStore } from '@df/module-advanced-commerce';
import { AdvancedCommerceTrackClickDirective } from '@df/module-advanced-commerce/track/advanced-commerce-track-click.directive';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ImageModule } from '@df/module-image/image.module';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackCategoryProductClickDirective } from '@df/module-klevu-search/klevu-track-category-product-click.directive';
import { KlevuTrackClickDirective } from '@df/module-klevu-search/klevu-track-click.directive';
import { KlevuTrackRecommendationsProductClickDirective } from '@df/module-klevu-search/klevu-track-recommendations-product-click.directive';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { findMediaByLabel } from '@df/ui/common/find-media-by-label.pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { RatingComponent } from '@df/ui/common/rating.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import compact from 'lodash-es/compact';
import { Subscription } from 'rxjs';
import SwiperCore, { Pagination } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { AdvancedCommerceListingAdvertComponent } from '../../../module/advanced-commerce/listing/advert/advanced-commerce-listing-advert.component';
import { EGtmAddToCartVia } from '../../../module/gtm/gtm.service.decorator';
import { XnotifFormComponent } from '../../../module/xnotif/xnotif-form.component';
import { IfNotTradeStoreDirective } from '../../../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../../../multisite/trade/if-trade-store.directive';
import { SwiperDotsComponent } from '../../../ui/widget/swiper-dots/swiper-dots.component';
import { LocalProduct } from '../../model/product/product.decorator';
import { LpAddToBasketComponent } from '../basket/lp-add-to-basket/lp-add-to-basket.component';
import { WishlistRemoveComponent } from '../wishlist/wishlist-remove.component';
import { WishlistToggleComponent } from '../wishlist/wishlist-toggle.component';
import { LpProductConfigurableOptionsComponent } from './configurable-options/lp-product-configurable-options.component';
import { ProductConfigurableOptionsStockComponent } from './configurable-options/stock/product-configurable-options-stock.component';
import { LpProductInStockDateComponent } from './lp-product-in-stock-date.component';
import { ProductPriceComponent } from './product-price/product-price.component';
import { ProductPriceTradeComponent } from './product-price/trade/product-price-trade.component';
import { ProductQuickBuyComponent } from './product-quick-buy.component';
SwiperCore.use([Pagination]);
export interface ListingImage {
    image: string;
    index?: number;
    video?: string;
}
const MEDIA_LABEL_BANNER_MOBILE = ['banner@s'];
const MEDIA_LABEL_BASE = ['base'];
const MEDIA_LABEL_SECONDARY = ['secondary'];
const MEDIA_LABEL_LANDSCAPE = ['landscape'];

export const OPEN_PREVIEW_EVENT = 'product.openPreview';
@Component({
    selector: 'product',
    templateUrl: 'product.html',
    styleUrls: ['./product.scss'],
    standalone: true,
    imports: [
        AdvancedCommerceListingAdvertComponent,
        AdvancedCommerceTrackClickDirective,
        DfModuleImageModule,
        DfUiModule,
        Ga4TrackClickDirective,
        HrefComponent,
        IfNotTradeStoreDirective,
        IfNotTradeStoreDirective,
        IfTradeStoreDirective,
        ImageModule,
        KlevuTrackClickDirective,
        KlevuTrackCategoryProductClickDirective,
        KlevuTrackRecommendationsProductClickDirective,
        LpAddToBasketComponent,
        LpProductConfigurableOptionsComponent,
        LpProductInStockDateComponent,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        OffClickModule,
        OptionLabelPipe,
        ProductConfigurableOptionsStockComponent,
        ProductPriceComponent,
        ProductPriceTradeComponent,
        ProductQuickBuyComponent,
        RatingComponent,
        RouterLink,
        ShellComponent,
        SizeClassDirective,
        SwiperDotsComponent,
        SwiperModule,
        TranslationModule,
        WishlistRemoveComponent,
        WishlistToggleComponent,
        XnotifFormComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductComponent extends ProductComponentCrosssellsDef<LocalProduct<IProductDataV2>> implements OnInit, OnChanges, OnDestroy {
    private renderer2 = inject(Renderer2);
    canSlide = false;
    showSwiper = false;
    carouselImages: ListingImage[] = [];
    /**
     * Prepare preview
     */
    readonly _previewStatus = new Status();
    /**
     * If component is ready to show preview
     */
    previewReady = false;
    previewOpened = false;

    protected swatchesCount: number = 0;
    /**
     * If preview was requested (button was clicked)
     */
    protected requestedPreview = false;
    /**
     * If the quick buy button should be visible
     */
    isQuickAddBtnVisible = false;
    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;
    @Input()
    klevuRecommendationId?: string;
    @ViewChild(SwiperComponent)
    swiper?: SwiperComponent;

    @Input()
    searchTerm?: string;
    @Input()
    wishlistItem?: WishlistItem;
    @Input()
    forceOneImage?: boolean;
    @Input()
    canShowTieredPrice?: boolean;
    @Input()
    showQuickBuyAnimation?: boolean;
    @Input()
    ga4MenuNavigation?: Gtm.IGA4TrackData;
    @Input()
    inCarousel = false;
    @Input()
    acClickCode?: string;
    @ViewChild('trayModalContent')
    trayModalContentTemplateRef?: TemplateRef<any>;
    modalRef?: ModalRef<TemplateRef<any>>;

    @Input()
    acItem?: AdvancedCommerceStore.Items.Item;

    protected isBrowser: boolean;
    /**
     * Changed on first interaction with the component
     * add extra content that we dont need initially
     */
    protected hadInteraction = false;
    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        elementRef: ElementRef,
        protected modalService: ModalService
    ) {
        super(changeDetectorRef, app, timeoutService, elementRef);
        this.isBrowser = app.isPlatformBrowser;
    }
    override async ngOnInit() {
        await super.ngOnInit();
        this.listingSubscription = this.listing?.subscribe(() => this.setSwiperVisibility());
        this.subscriptions.add(this._previewStatus.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.events
                .on(OPEN_PREVIEW_EVENT)
                .subscribe((event: { initiator: ProductComponent }) => this.previewOpened && this !== event.initiator && this.close())
        );
    }

    ngAfterViewInit() {
        this.renderer2.setStyle(this.elementRef.nativeElement as HTMLElement, 'visibility', 'visible');
    }
    override ngOnDestroy() {
        this.unsubscribe();
        super.ngOnDestroy();
    }
    override onModelChange() {
        this.canSlide = (this.client.isS || this.client.isM) && this.client.isTouchy;
        this.isQuickAddBtnVisible = this.client.isTouchy;
        this.updateImages();
        super.onModelChange();
        this.swatchesCount = this.getSwatchesCount();
        this.modalRef?.containerInstance?.['changeDetectorRef'].detectChanges();
    }
    unsubscribe() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
            this.listingSubscription = undefined;
        }
    }
    get bannerImage() {
        const image = this.model?.data?.image;
        const bannerMobileImage = findMediaByLabel(this.model?.data?.media || [], MEDIA_LABEL_BANNER_MOBILE, false);
        return this.client.isS ? bannerMobileImage || image : image;
    }
    /**
     * Sets if the swiper should be shown
     */
    setSwiperVisibility() {
        this.showSwiper =
            this.carouselImages?.length <= 1 || this.forceOneImage
                ? false
                : this.app.client.isS && this.listing?.sizeState === 2
                  ? false
                  : true;
        this.detectChanges();
    }
    /**
     * Sets images for swiper use
     */
    private updateImages() {
        const _media = this.model?.media || [];
        const media = filterMediaByLabel(_media, MEDIA_LABEL_BANNER_MOBILE, true) || [];
        const baseLabel = filterMediaByLabel(media, MEDIA_LABEL_BASE, false) || [];
        const noBaseAndLandscapeLabel = filterMediaByLabel(media, [...MEDIA_LABEL_BASE, ...MEDIA_LABEL_LANDSCAPE], true) || [];
        const secondaryLabel = filterMediaByLabel(media, MEDIA_LABEL_SECONDARY, false) || [];
        this.carouselImages = (
            this.app.client.isS ? compact([...baseLabel, ...noBaseAndLandscapeLabel]) : compact([...baseLabel, ...secondaryLabel])
        ).filter(el => !el.video && !el.labels?.includes('3dviewer'));
        this.setSwiperVisibility();
    }
    override onMouseIn() {
        if (!this.client.isTouchy) {
            // load v3 for preview
            this.loadPreview();
            this.isQuickAddBtnVisible = true;
            this.detectChanges();
        }
    }
    onMouseOut() {
        if (!this.client.isTouchy) {
            this.isQuickAddBtnVisible = false;
            this.detectChanges();
        }
    }
    onImageMouseIn() {
        this.onInteraction();
        if (!this.client.isTouchy && this.showSwiper && this.swiper?.swiperRef?.slides.length && this.swiper.swiperRef.slides.length > 1) {
            this.swiper.swiperRef.slideTo(1);
        }
    }
    onImageMouseOut() {
        if (!this.client.isTouchy && this.showSwiper && this.swiper?.swiperRef?.slides.length && this.swiper.swiperRef.slides.length > 1) {
            this.swiper.swiperRef.slideTo(0);
        }
    }
    /**
     * Load v3 data for preview
     */
    private loadPreview(): Promise<void> {
        if (this._previewStatus.busy && this._previewStatus.promise) {
            return this._previewStatus.promise;
        }
        return (this._previewStatus.promise = new Promise<void>(resolve => {
            if (this.previewReady) {
                resolve();
            } else {
                this.app.request.send(
                    this._previewStatus,
                    async () => this.model.parent.load(3, { safe: true, dependencyLevel: EdependencyLevel.details }),
                    () => {
                        this.previewReady = true;
                        this.detectChanges();
                        resolve();
                    },
                    resolve
                );
            }
        }));
    }
    /**
     * If preview options can be used (otherwise simple product approach is taken)
     */
    get isPreviewable() {
        if (this.parent?.hasSizeOption) {
            const options = this.parent.data?.options?.configurable;
            if (options) {
                const optionsQty = Object.values(options).length;
                switch (optionsQty) {
                    case 1: // size is the onlyu option
                        return true;
                    case 2:
                        return !!options['color'];
                }
            }
        }
        return false;
    }
    close() {
        this.togglePreview(false);
    }

    togglePreview(to: boolean) {
        if (to) {
            this._previewStatus.busy = true;
            this.events.broadcast(OPEN_PREVIEW_EVENT, { initiator: this });
        }
        const toggle = () => {
            this.previewOpened = to;
            this._previewStatus.busy = false;
            if (this.client.isS) {
                if (this.previewOpened && this.trayModalContentTemplateRef) {
                    this.modalRef = this.modalService.openBottom<TemplateRef<any>>(this.trayModalContentTemplateRef, {
                        width: '100%',
                        height: '100%',
                        modalClass: 'modal-container-overflow-hidden',
                        onClose: () => {
                            this.previewOpened = false;
                            this.modalRef = undefined;
                            this.detectChanges();
                        }
                    });
                } else {
                    this.modalRef?.close();
                }
            }
            this.detectChanges();
        };
        if (to) {
            this.loadPreview().finally(toggle);
        } else {
            toggle();
        }
    }
    protected get gtmAddToCartVia(): EGtmAddToCartVia {
        return this.inCarousel ? EGtmAddToCartVia.carouselQuick : EGtmAddToCartVia.quick;
    }
    protected get ga4MenuNavigationData(): Gtm.IGA4TrackData | undefined {
        return (
            this.ga4MenuNavigation && {
                name: this.ga4MenuNavigation.name,
                details: { ...this.ga4MenuNavigation.details, nav_item: this.model.data?.name }
            }
        );
    }
    get klevuSearchTrackClick(): KlevuSearch.ITrackSearchProductClickData | undefined {
        if (this.searchTerm) {
            return {
                product: this.model,
                term: this.searchTerm
            };
        }
        return undefined;
    }
    get klevuCategoryProductTrackClick(): KlevuSearch.ITrackCategoryProductClickData | undefined {
        if (this.listing?.config.source?.type === Category.type) {
            return {
                product: this.model,
                category: this.listing.config.source,
                position: this.listPosition && this.listPosition > 0 ? this.listPosition - 1 : undefined,
                searchType: this.listing?.sort?.getSelection()?.engine === 'klevu' ? KlevuSearch.catNavSearchType : undefined
            };
        }
        return undefined;
    }
    get klevuRecommendationsProductClickData():
        | KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>
        | undefined {
        if (this.klevuRecommendationId && this.model && typeof this.listPosition === 'number') {
            return <KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>>{
                event: KlevuSearch.EAnalyticsRecommendationEvent.select,
                eventListId: this.klevuRecommendationId,
                products: [{ model: this.model, position: this.listPosition }]
            };
        }
        return undefined;
    }
    @HostListener('click')
    @HostListener('touchmove')
    protected override onInteraction() {
        if (!this.hadInteraction) {
            this.hadInteraction = true;
            this.detectChanges();
        }
    }

    override getSwatchesCount(): number {
        if (!this.parent || !this.swatchAttribute) {
            return 0;
        }
        const map = this.parent.data?.stockMap?.[this.swatchAttribute];
        if (!map) {
            return super.getSwatchesCount();
        }
        return Object.values(map).filter(i => i.length > 0).length;
    }

    get isComingSoonView(): boolean {
        return this.model.isComingSoon || this.model.isComingSoonForced;
    }
}
